import React from "react";
import { useNavigate } from "react-router-dom";
import { FaMapMarkerAlt, FaCalendarAlt } from "react-icons/fa";
import { Card, Badge } from "../UI/Card";
import { StarDisplay } from "./StarRating";
import { formatINR } from "./marketplaceUtils";

const PLACEHOLDER_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='260'><rect width='100%' height='100%' fill='%23f6f7f9'/><text x='50%' y='50%' font-family='sans-serif' font-size='16' fill='%238993a4' text-anchor='middle' dominant-baseline='middle'>Rasutrip</text></svg>`
  );

// One card renders either a marketplace `service` or a `package` — the two share almost
// the same summary shape (title, partner, rating, price) so a single presentational
// component keeps the browse grid visually consistent instead of forking styles.
const MarketplaceCard = ({ item, type }) => {
  const navigate = useNavigate();
  const isPackage = type === "package";

  const image =
    (isPackage ? item.images : item.photos)?.find(Boolean) || PLACEHOLDER_IMG;
  const price = isPackage ? item.price : item.basePrice;
  const partner = item.partnerId || {};

  const goToDetail = () =>
    navigate(isPackage ? `/marketplace/packages/${item._id}` : `/marketplace/services/${item._id}`);

  return (
    <Card
      hover
      className="overflow-hidden cursor-pointer flex flex-col"
      onClick={goToDetail}
    >
      <div className="relative h-44 w-full overflow-hidden bg-ink-50">
        <img
          src={image}
          alt={item.title}
          className="h-full w-full object-cover"
          onError={(e) => {
            e.currentTarget.src = PLACEHOLDER_IMG;
          }}
        />
        <div className="absolute top-3 left-3 flex gap-1.5">
          <Badge tone="dark">{isPackage ? "Package" : item.category || "Service"}</Badge>
          {isPackage && item.durationDays && (
            <Badge tone="brand">{item.durationDays}D</Badge>
          )}
        </div>
      </div>

      <div className="p-4 flex flex-col gap-2 flex-1">
        <h3 className="font-bold text-ink-900 text-[15px] leading-snug line-clamp-2">
          {item.title}
        </h3>

        {!!(item.destinationCities || []).length && (
          <p className="flex items-center gap-1.5 text-xs text-ink-500">
            <FaMapMarkerAlt className="shrink-0" />
            <span className="truncate">{item.destinationCities.join(", ")}</span>
          </p>
        )}

        <p className="text-xs text-ink-500 truncate">
          {partner.businessName || "Rasutrip Partner"}
          {partner.city ? ` · ${partner.city}` : ""}
        </p>

        {(partner.ratingCount || 0) > 0 ? (
          <StarDisplay value={partner.ratingAvg} count={partner.ratingCount} />
        ) : (
          <span className="text-xs text-ink-400">New partner</span>
        )}

        <div className="mt-auto pt-2 flex items-end justify-between">
          <div>
            <p className="text-[11px] text-ink-400 uppercase tracking-wide font-semibold">
              {isPackage ? "From" : "Starting at"}
            </p>
            <p className="text-lg font-extrabold text-brand-600">{formatINR(price)}</p>
          </div>
          <span className="text-xs font-semibold text-brand-600">View details →</span>
        </div>
      </div>
    </Card>
  );
};

export default MarketplaceCard;
